import React, { useEffect, useMemo, useState } from 'react';

import edit from '@amzn/meridian-tokens/base/icon/edit';
import trash from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Table, { type TableSortDirection } from '@amzn/meridian/table/table';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import { type NonPhysicalTask } from 'src/services/tasks/types';
import { type AccommodationTypeName } from 'src/types/accommodationTypes';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

import { COLUMNS } from '../constants';

// **** Types **** //

export interface NonPhysicalTaskTableRow {
  id: number;
  accommodationType: AccommodationTypeName;
  name: string;
  region: string;
  country: string;
  description: string;
  allowsDuration: boolean;
  allowsFrequency: boolean;
  allowsFreeFormText: boolean;
}

/**
 * Convert task object to a table row
 */
export const nonPhysicalTaskToTableRow = (task: NonPhysicalTask): NonPhysicalTaskTableRow => {
  const typeName = task.accommodationType.typeName.toUpperCase();

  return {
    id: task.id,
    name: task.name,
    accommodationType: typeName as AccommodationTypeName,
    description: task.description ?? '',
    region: task.region?.name ?? '',
    country: task.region?.country?.name ?? '',
    allowsDuration: task.allowsDuration,
    allowsFrequency: task.allowsFrequency,
    allowsFreeFormText: task.allowsFreeFormText,
  };
};

interface NonPhysicalTaskTableProps {
  tableData: NonPhysicalTaskTableRow[];
  searchField: string;
  onEdit: (data: NonPhysicalTaskTableRow) => void;
  onDelete: (data: NonPhysicalTaskTableRow) => void;
}

interface OnSortProps {
  sortColumn: keyof NonPhysicalTaskTableRow;
  sortDirection: TableSortDirection;
}

// **** Components **** //

/**
 * Default component
 */
const NonPhysicalTaskTable = ({ tableData, searchField, onEdit, onDelete }: NonPhysicalTaskTableProps) => {
  const [sortColumn, setSortColumn] = useState<keyof NonPhysicalTaskTableRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [perPage, setPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter, sorting, and pagination
  const { paginatedData: filteredAndSortedTableData, totalPages } = useMemo(
    () =>
      filterSortAndPaginateTableData(tableData, searchField, sortColumn, sortDirection, currentPage, ['id'], perPage),
    [tableData, searchField, sortColumn, sortDirection, currentPage, perPage],
  );

  // Go back to first page when filter/sort
  useEffect(() => {
    setCurrentPage(1);
  }, [perPage, searchField, sortColumn, sortDirection]);

  const onSort = ({ sortColumn, sortDirection }: OnSortProps) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  return (
    <>
      {/* Table */}
      <Box type="outline">
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showDividers={true}
          fixHeaderRows={true}
          rowComponents={[UncontrolledExpandableRow, TableRow]}
        >
          {/* Header */}
          <TableRow>
            {COLUMNS.map(({ sortKey, name, width }, idx) => (
              <TableCell width={width} key={`${idx}-${sortKey}`} sortColumn={sortKey}>
                {name}
              </TableCell>
            ))}
          </TableRow>

          {/* Body */}
          {filteredAndSortedTableData.map((row) => (
            <UncontrolledExpandableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.region}</TableCell>
              <TableCell>{row.country}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.accommodationType}</TableCell>
              <TableCell>
                <Button type="icon" aria-label={`Edit ${row.name}`} onClick={() => onEdit(row)}>
                  <Icon tokens={edit} />
                </Button>
              </TableCell>
              <TableCell>
                <Button type="icon" aria-label={`Delete ${row.name}`} onClick={() => onDelete(row)}>
                  <Icon tokens={trash} />
                </Button>
              </TableCell>
            </UncontrolledExpandableRow>
          ))}
        </Table>
      </Box>

      {/* Pagination */}
      {!!totalPages && (
        <PaginationWithDropdown
          totalPages={totalPages}
          currentPage={currentPage}
          perPage={perPage}
          setCurrentPage={setCurrentPage}
          setPerPage={setPerPage}
        />
      )}
    </>
  );
};

// **** Export **** //

export default NonPhysicalTaskTable;
